import type { FC, ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';

interface PageHeaderProps {
  /** Lucide icon component */
  icon?: LucideIcon;
  /** Main page title — always text-xl font-bold text-slate-100 */
  title: string;
  /** Optional subtitle below title */
  subtitle?: string;
  /** Optional badge/chip next to title (e.g. standard badge, count pill) */
  badge?: ReactNode;
  /** Optional right-side action buttons */
  actions?: ReactNode;
  /** Override icon colour — defaults to text-rb-brand */
  iconColor?: string;
}

/** Consistent page header used on every page of the application.
 *  Produces: icon + title (text-xl font-bold text-slate-100) + optional subtitle + optional actions.
 */
const PageHeader: FC<PageHeaderProps> = ({
  icon: Icon,
  title,
  subtitle,
  badge,
  actions,
  iconColor = 'text-rb-brand',
}) => (
  <div className="flex items-start justify-between gap-4 flex-wrap">
    <div className="flex items-center gap-3 min-w-0">
      {Icon && (
        <div className="w-9 h-9 rounded-xl bg-rb-brand/10 border border-rb-brand/20 flex items-center justify-center shrink-0">
          <Icon className={`w-[18px] h-[18px] ${iconColor}`} />
        </div>
      )}
      <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <h1 className="text-xl font-bold text-slate-100 leading-tight tracking-tight">{title}</h1>
          {badge}
        </div>
        {subtitle && (
          <p className="text-sm text-slate-500 mt-0.5 leading-snug">{subtitle}</p>
        )}
      </div>
    </div>
    {actions && (
      <div className="flex items-center gap-2 shrink-0 flex-wrap">{actions}</div>
    )}
  </div>
);

export default PageHeader;
